// Journal capture handler — cloned from focusCaptureHandler. Maps the
// propose_journal_log proposal (raw spoken diary entry) through the
// journal-extract edge fn into a JournalPrefill, then opens JournalLogModal for
// review. Behavior parity: 402 -> shared quota gate; no usable result -> null
// (silent); the MODAL owns the DB write (useJournal.saveEntry, source 'voice').

import JournalLogModal, { type JournalPrefill } from "@/components/journal/JournalLogModal";
import { supabase } from "@/integrations/supabase/client";
import type { JournalMode } from "@/hooks/useJournal";
import type { ModuleCaptureHandler } from "@/lib/capture/types";

// Shape journal-extract returns (mirrors the edge fn schema).
interface ExtractedJournal {
  content: string;
  mode: string;
  mood: number | null;
  confidence: number;
}

const VALID_MODES: JournalMode[] = ["free", "reflection", "gratitude"];
function toMode(m: string): JournalMode {
  return (VALID_MODES as string[]).includes(m) ? (m as JournalMode) : "free";
}

export const journalCaptureHandler: ModuleCaptureHandler<JournalPrefill> = {
  tool: "propose_journal_log",

  getRawText: (proposal) =>
    proposal.tool === "propose_journal_log"
      ? (proposal.args.raw_text ?? "").trim()
      : "",

  extract: async (rawText, proposal, ctx) => {
    const { data, error } = await supabase.functions.invoke("journal-extract", {
      body: {
        transcript: rawText,
        language: ctx.lang,
      },
    });
    if (error) {
      if (await ctx.surfaceQuota(error)) return null;
      console.warn("[capture/journal] journal-extract failed", error);
      return null;
    }

    const r = data as ExtractedJournal | null;
    if (!r || typeof r.content !== "string" || typeof r.confidence !== "number") {
      console.info("[capture/journal] journal-extract returned no usable result");
      return null;
    }
    // Not-a-journal guard — abort silently (Sonnet's text already rendered).
    if (r.confidence < 0.25 || !r.content.trim()) {
      console.info("[capture/journal] transcript not recognized as a journal entry");
      return null;
    }

    const mood =
      typeof r.mood === "number" && r.mood >= 1 && r.mood <= 5 ? Math.round(r.mood) : null;
    return {
      content: r.content.trim(),
      mode: toMode(r.mode),
      mood,
      source: "voice",
      // Per-turn idempotency (focus/workout parity) — a voice double-confirm
      // replays instead of duplicating (journal_entries_client_action_id_key).
      client_action_id: "mirror_msg_" + proposal.action_id,
      transcriptRaw: rawText,
    };
  },

  renderModal: ({ open, prefill, onClose, onCreated }) => (
    <JournalLogModal open={open} prefill={prefill} onClose={onClose} onCreated={onCreated} />
  ),
};
